import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Add,
  Delete,
  DragIndicator,
  ExpandMore,
  AutoAwesome,
  Refresh,
  Warning,
  CheckCircle,
  Info
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

interface SafetyMeasure {
  id: string;
  measure_number: number;
  measure_type: 'iasm' | 'rasm' | 'general';
  description: string;
  isolation_method_id?: number;
  energy_source_id?: number;
  verification_required: boolean;
  verification_method: string;
  estimated_time_minutes: number;
  tools_required: string;
  ppe_required: string;
  competency_required: string;
  can_be_removed_for_testing: boolean;
  affected_equipment_codes?: string[];
  isolation_rationale?: string;
  is_auto_generated?: boolean;
}

interface AnalysisResults {
  equipment_analyzed: number;
  energy_sources_count: number;
  isolation_methods_count: number;
  suggested_safety_measures: SafetyMeasure[];
  analysis_summary: {
    isolation_complexity: string;
    total_estimated_time: number;
    energy_types_involved: string[];
  };
}

interface SafetyMeasuresManagerProps {
  equipmentIds: number[];
  safetyMeasures: SafetyMeasure[];
  onSafetyMeasuresChange: (measures: SafetyMeasure[]) => void;
  disabled?: boolean;
}

const SafetyMeasuresManager: React.FC<SafetyMeasuresManagerProps> = ({
  equipmentIds,
  safetyMeasures,
  onSafetyMeasuresChange,
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Auto-analyze when equipment changes
  useEffect(() => {
    if (equipmentIds.length > 0) {
      analyzeIsolations();
    } else {
      setAnalysisResults(null);
    }
  }, [equipmentIds]);

  const analyzeIsolations = useCallback(async () => {
    if (equipmentIds.length === 0) return;

      setLoading(true);
      setError(null);
      
    try {
      // Get CSRF token first
      let csrfToken: string | null = null;
      try {
        const csrfResponse = await fetch('/api/v1/users/auth/csrf_token/', {
          method: 'GET',
          credentials: 'include',
        });
        if (csrfResponse.ok) {
          const csrfData = await csrfResponse.json();
          csrfToken = csrfData.csrf_token;
        }
      } catch (csrfError) {
        console.warn('Failed to get CSRF token:', csrfError);
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch('/api/v1/sites/site-management/analyze_isolations/', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          equipment_ids: equipmentIds
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.statusText}`);
      }

      const data = await response.json();
      setAnalysisResults(data.analysis_results);
      
      // Show success message
      console.log(`✅ ${data.message}`);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze isolations');
      console.error('Isolation analysis error:', err);
    } finally {
      setLoading(false);
    }
  }, [equipmentIds]);

  const applyAutoSuggestions = useCallback(() => {
    if (!analysisResults?.suggested_safety_measures) return;

    // Mark existing auto-generated measures for removal
    const manualMeasures = safetyMeasures.filter(m => !m.is_auto_generated);
    
    // Add new auto-generated measures
    const autoMeasures = analysisResults.suggested_safety_measures.map((measure, index) => ({
      ...measure,
      id: `auto_${Date.now()}_${index}`,
      is_auto_generated: true
    }));

    // Combine and renumber
    const allMeasures = [...autoMeasures, ...manualMeasures];
    const renumberedMeasures = allMeasures.map((measure, index) => ({
      ...measure,
      measure_number: index + 1
    }));

    onSafetyMeasuresChange(renumberedMeasures);
  }, [analysisResults, safetyMeasures, onSafetyMeasuresChange]);

  const addManualMeasure = useCallback(() => {
    const newMeasure: SafetyMeasure = {
      id: `manual_${Date.now()}`,
      measure_number: safetyMeasures.length + 1,
      measure_type: 'iasm',
      description: '',
      verification_required: true,
      verification_method: '',
      estimated_time_minutes: 15,
      tools_required: '',
      ppe_required: '',
      competency_required: '',
      can_be_removed_for_testing: false,
      is_auto_generated: false
    };

    onSafetyMeasuresChange([...safetyMeasures, newMeasure]);
  }, [safetyMeasures, onSafetyMeasuresChange]);

  const removeMeasure = useCallback((id: string) => {
    const updatedMeasures = safetyMeasures
      .filter(m => m.id !== id)
      .map((measure, index) => ({
        ...measure,
        measure_number: index + 1
      }));
    
    onSafetyMeasuresChange(updatedMeasures);
  }, [safetyMeasures, onSafetyMeasuresChange]);

  const updateMeasure = useCallback((id: string, field: string, value: any) => {
    const updatedMeasures = safetyMeasures.map(measure =>
      measure.id === id ? { ...measure, [field]: value } : measure
    );
    
    onSafetyMeasuresChange(updatedMeasures);
  }, [safetyMeasures, onSafetyMeasuresChange]);

  const handleDragEnd = useCallback((result: any) => {
    if (!result.destination) return;

    const items = Array.from(safetyMeasures);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Renumber after reordering
    const renumberedItems = items.map((item, index) => ({
      ...item,
      measure_number: index + 1
    }));

    onSafetyMeasuresChange(renumberedItems);
  }, [safetyMeasures, onSafetyMeasuresChange]);

  const renderMeasureTypeIcon = (measureType: string) => {
    switch (measureType) {
      case 'iasm':
        return <CheckCircle color="primary" />;
      case 'rasm':
        return <Warning color="warning" />;
      default:
        return <Info color="info" />;
    }
  };

  const renderAnalysisSummary = () => {
    if (!analysisResults) return null;

    const { analysis_summary } = analysisResults;
    
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AutoAwesome sx={{ mr: 1, verticalAlign: 'middle' }} />
            Isolation Analysis Summary
          </Typography>
          
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip 
              label={`${analysisResults.equipment_analyzed} Equipment`} 
              variant="outlined" 
            />
            <Chip 
              label={`${analysisResults.energy_sources_count} Energy Sources`} 
              variant="outlined" 
            />
            <Chip 
              label={`${analysisResults.isolation_methods_count} Methods`} 
              variant="outlined" 
            />
            <Chip 
              label={`~${analysis_summary.total_estimated_time} min`} 
              variant="outlined" 
              color="info"
            />
            <Chip 
              label={`${analysis_summary.isolation_complexity} complexity`} 
              variant="outlined" 
              color={
                analysis_summary.isolation_complexity === 'simple' ? 'success' :
                analysis_summary.isolation_complexity === 'moderate' ? 'warning' : 'error'
              }
            />
          </Stack>

          {analysis_summary.energy_types_involved.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Energy Types: {analysis_summary.energy_types_involved.join(', ')}
        </Typography>
      </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderSafetyMeasure = (measure: SafetyMeasure, index: number) => (
    <Draggable key={measure.id} draggableId={measure.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          variant="outlined"
          sx={{
            mb: 1,
            opacity: snapshot.isDragging ? 0.8 : 1,
            backgroundColor: measure.is_auto_generated ? 'action.hover' : 'background.paper'
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Box {...provided.dragHandleProps}>
                <DragIndicator color="action" />
              </Box>
              
              <Box sx={{ flexGrow: 1 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  {renderMeasureTypeIcon(measure.measure_type)}
                  <Typography variant="subtitle2">
                    {measure.measure_number}. Safety Measure
                  </Typography>
                  
                  {measure.is_auto_generated && (
                    <Chip 
                      label="Auto-Generated" 
                      size="small" 
                      color="primary" 
                      variant="outlined" 
                    />
                  )}
                  
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={measure.measure_type}
                      onChange={(e) => updateMeasure(measure.id, 'measure_type', e.target.value)}
                      disabled={disabled}
                    >
                      <MenuItem value="iasm">IASM</MenuItem>
                      <MenuItem value="rasm">RASM</MenuItem>
                      <MenuItem value="general">General</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <TextField
                  fullWidth
                  label="Description"
                  value={measure.description}
                  onChange={(e) => updateMeasure(measure.id, 'description', e.target.value)}
                  multiline
                  rows={2}
                  disabled={disabled}
                  sx={{ mb: 2 }}
                />

                {measure.affected_equipment_codes && measure.affected_equipment_codes.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Affects Equipment:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {measure.affected_equipment_codes.map((code, idx) => (
                        <Chip key={idx} label={code} size="small" />
                      ))}
                    </Stack>
                  </Box>
                )}

                {measure.isolation_rationale && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      {measure.isolation_rationale}
                    </Typography>
                  </Alert>
                )}

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="body2">Additional Details</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={measure.verification_required}
                            onChange={(e) => updateMeasure(measure.id, 'verification_required', e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label="Verification Required"
                      />
                      
                      <TextField
                        label="Verification Method"
                        value={measure.verification_method}
                        onChange={(e) => updateMeasure(measure.id, 'verification_method', e.target.value)}
                        disabled={disabled}
                        multiline
                        rows={2}
                      />
                      
                      <TextField
                        label="Estimated Time (minutes)"
                        type="number"
                        value={measure.estimated_time_minutes}
                        onChange={(e) => updateMeasure(measure.id, 'estimated_time_minutes', parseInt(e.target.value) || 0)}
                        disabled={disabled}
                      />
                      
                      <TextField
                        label="Tools Required"
                        value={measure.tools_required}
                        onChange={(e) => updateMeasure(measure.id, 'tools_required', e.target.value)}
                        disabled={disabled}
                      />
                      
                      <TextField
                        label="PPE Required"
                        value={measure.ppe_required}
                        onChange={(e) => updateMeasure(measure.id, 'ppe_required', e.target.value)}
                        disabled={disabled}
                      />
                      
                      <TextField
                        label="Competency Required"
                        value={measure.competency_required}
                        onChange={(e) => updateMeasure(measure.id, 'competency_required', e.target.value)}
                        disabled={disabled}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={measure.can_be_removed_for_testing}
                            onChange={(e) => updateMeasure(measure.id, 'can_be_removed_for_testing', e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label="Can be removed for testing"
                      />
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>

              <IconButton
                onClick={() => removeMeasure(measure.id)}
                color="error"
                disabled={disabled}
              >
                <Delete />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );

  return (
    <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Safety Measures</Typography>
        <Stack direction="row" spacing={1}>
          {equipmentIds.length > 0 && (
            <>
              <Tooltip title="Re-analyze equipment isolations">
                <IconButton 
                  onClick={analyzeIsolations} 
                  disabled={loading || disabled}
                  color="primary"
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              
              {analysisResults && (
            <Button
                  variant="outlined"
                  startIcon={<AutoAwesome />}
                  onClick={applyAutoSuggestions}
                  disabled={disabled || !analysisResults.suggested_safety_measures.length}
                  color="primary"
                >
                  Apply Suggestions ({analysisResults.suggested_safety_measures.length})
            </Button>
              )}
            </>
          )}
          
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addManualMeasure}
            disabled={disabled}
          >
            Add Manual
          </Button>
        </Stack>
      </Box>

      {loading && (
        <Box display="flex" alignItems="center" justifyContent="center" py={3}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Analyzing equipment isolations...</Typography>
        </Box>
      )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      {equipmentIds.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select equipment to automatically analyze isolation requirements and generate safety measures.
        </Alert>
      )}

      {renderAnalysisSummary()}

      {safetyMeasures.length === 0 ? (
        <Alert severity="info">
          No safety measures defined yet. Add equipment to get automatic suggestions or add manual measures.
        </Alert>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="safety-measures">
            {(provided) => (
              <Box {...provided.droppableProps} ref={provided.innerRef}>
                {safetyMeasures.map((measure, index) => 
                  renderSafetyMeasure(measure, index)
                )}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Box>
  );
};

export default SafetyMeasuresManager; 